// src/components/Simulations.js
import React, { useState, useEffect, useRef } from "react";
import "./UploadFiles.css";
import { MdOutlineFileUpload } from "react-icons/md";
import { MdOutlineFileDownload } from "react-icons/md";
import { MdDeleteForever } from "react-icons/md";
import Papa from "papaparse";

import { createQueue, deleteQueue, displayLog } from "./Queue";
import { simulateRun } from "./Simulate";
import LogDisplay from "./LogDisplay";
import { fetchResultsData, downloadResultsData } from "./ResultsDisplay";
import { useNavigation } from "../../../../contexts/NavigationContext";
import { simulationService } from "../../../../services/api/simulationService";
import { sysConfigApi } from "../../../../services/api/sysconfigService";
import { uploadFileApi } from "../../../../services/api/inputService";
import { PYTHON_API_BASE_URL } from "../../../../services/api/apiConstants";
import { runQueueName } from "../../utils/queueName";

// NEW IMPORT: Our History Import tab component
import HistoryImport from "./HistoryImport";

const Simulations = () => {
  // Add these ref declarations at the top of your component with other state declarations
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  // 1) NEW: State to track which tab is active
  const [activeTab, setActiveTab] = useState("generate");

  // --- Your existing states ---
  const [triggerProcessing, setTriggerProcessing] = useState(false);
  const [isDerLogExpanded, setIsDerLogExpanded] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({
    "Building Load": null,
    "Utility Tariff": null,
    "Solar Generation": null,
    "EV Availability": null,
  });
  const { setTimeSeriesFileName, selectedBranchId } = useNavigation();
  const [hasConnectedEVStorage, setHasConnectedEVStorage] = useState(false);
  const [uploadedJSONFile, setUploadedJSONFile] = useState(null);
  const [jsonFileError, setJSONFileError] = useState(null);
  const [displayedPreview, setDisplayedPreview] = useState(null);
  const [demandCharge, setDemandCharge] = useState("");
  const [reportingPeriod, setReportingPeriod] = useState("");
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [customer, setCustomer] = useState(null);
  const token = sessionStorage.getItem("accessToken");
  const [log, setLog] = useState("");
  const [timeseriesFile, setTimeseriesFile] = useState("");
  const [economicSummaryFile, setEconomicSummaryFile] = useState("");
  const [dailysummaryFile, setDailysummaryFile] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [simulationInProgress, setSimulationInProgress] = useState(false);
  const [simulationFailureMessage, setSimulationFailureMessage] = useState("");
  const [isDERRequired, setIsDERRequired] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);

  // Simulation results datasets.
  const [timeseriesFileData, setTimeseriesFileData] = useState("");
  const [economicSummaryFileData, setEconomicSummaryFileData] = useState("");
  const [dailysummaryadata, setdailysummarydata] = useState("");
  const [fetchedData, setFetchedData] = useState("");
  const [storageAccountName, setStorageAccountName] = useState("");
  const [customerSimulationName, setCustomerSimulationName] = useState("");
  
  const rawSimulationName = sessionStorage.getItem("simulationName"); // full name from blob, if set
  const microgridBase = sessionStorage.getItem("microgridName");
  const rawCustomerEmail =
    sessionStorage.getItem("selectedCustomerEmail") || "";

  const customerEmail = rawCustomerEmail.replace(/[@.]/g, "-");

  const simulationName =
    rawSimulationName || `${customerEmail}-${microgridBase}`;
  const datasetName = `${simulationName}-sysconfig.json`;

  const accountname = sessionStorage.getItem("storageAccountName");

  const handleButtonClick = async (timedata) => {
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL;
      const customerName = storageAccountName;
      const simulationName = customerSimulationName;
      const datasetName = timedata;

      if (!datasetName) {
        console.error(
          "handleButtonClick: timeseriesFile is not set, skipping call.",
        );
        return;
      }
      Papa.parse(datasetName, {
        header: true, // parses first row as keys
        skipEmptyLines: true,
        complete: function (results) {
         
        },
      });

      // Fetch the timeseries file content
      const csvText = await fetchResultsData(
        "Timeseries",
        datasetName,
        baseUrl,
        customerName,
        simulationName,
        token,
      );

      if (!csvText) {
        console.error("Failed to fetch timeseries file.");
        return;
      }

      // Pass data to DataProcessor
      setFetchedData(csvText);
      
      setTriggerProcessing(true);
    } catch (error) {
      console.error("Error fetching timeseries file:", error);
    }
  }

  useEffect(() => {
    const email = sessionStorage.getItem("selectedCustomerEmail");
    if (email) {
      setCustomer(email);

      // Fetch required data before enabling file upload
      Promise.all([
        checkForConnectedEVDevices(email),
        fetchStorageAccountName(selectedBranchId),
      ])
        .then(() => {
          // Convert email to simulation-friendly format
          const sanitizedEmail = email.replace(/[^a-zA-Z0-9]/g, "-");
          setCustomerSimulationName(simulationName);
          
          setIsDataFetched(true); // Mark data as fetched
        })
        .catch((error) => {
          console.error("Error fetching required data:", error);
          setIsDataFetched(false);
        });
    } else {
      console.error("No customer email found in session storage.");
    }
  }, []);

  // Update global timeSeriesFileName when timeseriesFile changes
  useEffect(() => {
    if (timeseriesFile && timeseriesFile !== "") {
    
      setTimeSeriesFileName(timeseriesFile);
    }
  }, [timeseriesFile, setTimeSeriesFileName]);

  const fetchStorageAccountName = async (branchId) => {
    const API_URL = `${PYTHON_API_BASE_URL}/storage-account/?branch_id=${branchId}`;

    try {
      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-User-ID": sessionStorage.getItem("UserID") || "",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch storage account name.");
      }

      const data = await response.json();

      if (data.storage_account) {
        setStorageAccountName(data.storage_account.storage_account_name);
      }
    } catch (error) {
      setStorageAccountName("");
    }
  };

  const fetchAndBuildSchema = async () => {
    try {
      const config = await sysConfigApi({
        accountname: accountname,
        simulationName: simulationName,
        datasetName: datasetName,
        token: token,
        method: "GET",
        body: null,
      });

      // Backend returns full Microgrid config; use it as-is or fallback to empty schema
      const hasValidConfig =
        config?.Microgrid?.meters &&
        Array.isArray(config.Microgrid.meters) &&
        config.Microgrid.meters.length > 0;
      const schema = hasValidConfig
        ? config
        : {
            Microgrid: {
              meters: [
                {
                  name: "",
                  make_and_model: "",
                  serial_number: "",
                  utility_tariff_csv: "",
                  building_load: { historical_load_csv: "" },
                  evs: [],
                  batteries: [],
                  solar_arrays: [],
                  fueled_generators: [],
                  uncontrollable_loads: [],
                },
              ],
            },
          };

      const schemaJson = JSON.stringify(schema, null, 2);
      const blob = new Blob([schemaJson], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "sysconfig.json";
      link.click();
    } catch (error) {
      console.error("Error fetching sysconfig:", error);
    }
  }

  const parseCSV = (csvText) => {
    const parsed = Papa.parse(csvText, { header: true });
    return parsed.data; // Return parsed data as an array of objects
  };

  // Download and display results.
  const handleDisplay = async (type, datasetName) => {
    try {
      if (displayedPreview === type) {
        // Already displayed → so hide it now
        setDisplayedPreview(null);
        return;
      }
      

      const baseUrl = process.env.REACT_APP_BASE_URL;
      const customerName = storageAccountName;
      const simulationName = customerSimulationName;

      const csvText = await fetchResultsData(
        type,
        datasetName,
        baseUrl,
        customerName,
        simulationName,
        token,
      );
      const parsedData = parseCSV(csvText);
      

      if (type === "Timeseries") {
        setTimeseriesFileData(parsedData);
      } else {
        setdailysummarydata(parsedData);
      }

      setDisplayedPreview(type);
     
    } catch (error) {
      console.error("handleDisplay: Error displaying data:", error);
      setErrorMessage(`Error displaying data: ${error.message}`);
    }
  };

  const handleDownload = async (type, datasetName) => {
   

    const baseUrl = process.env.REACT_APP_BASE_URL;

    const customerName = storageAccountName;
    const simulationName = customerSimulationName;

    try {
      await downloadResultsData(
        type,
        datasetName,
        baseUrl,
        customerName,
        simulationName,
        token,
      );
    } catch (error) {
      console.error("handleDownload: Error during download:", error);
      setErrorMessage(`Error downloading file: ${error.message}`);
    }
  };

 

  
  

  const handleFileJSONUpload = async (event) => {
    const fileInput = event.target;
    const file = fileInput.files[0];

    // Clear previous errors for DER System Configuration
    setErrorMessages((prev) => ({
      ...prev,
      "DER System Configuration": "",
    }));
    setUploadedJSONFile("");

    if (!file) {
      setErrorMessages((prev) => ({
        ...prev,
        "DER System Configuration": "No file selected.",
      }));
      return;
    }

    if (file.name.split(".").pop().toLowerCase() !== "json") {
      setErrorMessages((prev) => ({
        ...prev,
        "DER System Configuration": "Please upload a .json file only.",
      }));
      return;
    }

    try {
      // Set the uploaded file name
      setUploadedJSONFile(file.name);

      const customerName = storageAccountName;
      const simulationName = customerSimulationName;
      const datasetName = encodeURIComponent(file.name);

     

      const fileContent = await file.text();

      // Validate JSON structure before upload
      try {
        JSON.parse(fileContent);
      } catch (jsonError) {
        setErrorMessages((prev) => ({
          ...prev,
          "DER System Configuration":
            "Invalid JSON file. Please check the file content.",
        }));
        return;
      }

      const response = await sysConfigApi({ customerName, simulationName, datasetName, token, method: "POST", body: fileContent });

      if (!response.ok) {
        const errorText = await response.text();
        setErrorMessages((prev) => ({
          ...prev,
          "DER System Configuration": `Upload failed: ${errorText}`,
        }));
        return;
      }

      // Clear the error if upload is successful
      setErrorMessages((prev) => ({
        ...prev,
        "DER System Configuration": "",
      }));
    } catch (error) {
      setErrorMessages((prev) => ({
        ...prev,
        "DER System Configuration": `Error uploading file: ${error.message}`,
      }));
      console.error("Error uploading file:", error);
    } finally {
      // Reset the file input to allow re-uploading the same file
      fileInput.value = null;
    }
  };

  const handleFileUpload = async (event, label) => {
    if (!isDataFetched) {
      setErrorMessages((prev) => ({
        ...prev,
        [label]: "Fetching the customer's storage account name. Please wait...",
      }));
      return;
    }

    const fileInput = event.target; // Reference to the input element
    const file = fileInput.files[0];

    if (!file) {
      setUploadedFiles((prev) => ({ ...prev, [label]: null }));
      setErrorMessages((prev) => ({ ...prev, [label]: "No file selected." }));
      return;
    }

    if (file.name.split(".").pop().toLowerCase() !== "csv") {
      setUploadedFiles((prev) => ({ ...prev, [label]: null }));
      setErrorMessages((prev) => ({
        ...prev,
        [label]: "Only CSV files are allowed.",
      }));
      return;
    }

    try {
      const customerName = storageAccountName;
      const simulationName = customerSimulationName;
      const datasetName = encodeURIComponent(file.name);

     
     
      const fileContent = await file.text();

      const response = await uploadFileApi({
        customerName: customerName,
        simulationName: simulationName,
        datasetName: datasetName,
        token: token,
        fileContent: fileContent,
      });

      if (!response.ok) {
        const errorResponse = await response.json();

        // Check for specific error message related to date format
        if (
          errorResponse.message &&
          errorResponse.message.includes("time data")
        ) {
          throw new Error(
            'The date format in the file does not match the expected format "%m/%d/%y %H:%M". Please correct the file and try again.',
          );
        }

        throw new Error(`${errorResponse.message}`);
      }

      setUploadedFiles((prev) => ({ ...prev, [label]: file.name }));
      setErrorMessages("");
     
    } catch (error) {
      // Handle the error
      setUploadedFiles((prev) => ({ ...prev, [label]: null }));
      setErrorMessages((prev) => ({
        ...prev,
        [label]: error.message,
      }));
      console.error("Error uploading file:", error);
    } finally {
      // Reset the file input to allow re-uploading the same file
      fileInput.value = null;
    }
  };

  const truncateFileName = (fileName, maxLength = 50) => {
    if (fileName && fileName.length > maxLength) {
      return fileName.slice(0, maxLength) + "...";
    }
    return fileName;
  };

  const checkForConnectedEVDevices = async (email) => {
    try {
      const config = await sysConfigApi({
        accountname,
        simulationName,
        datasetName,
        token,
        method: "GET",
        body: null,
      });

      // Derive from full Microgrid config: require at least one *connected* production or storage device
      const isConnected = (d) => (d?.status ?? "connected") === "connected";
      const meters = config?.Microgrid?.meters ?? [];
      const hasEVs = meters.some((m) =>
        (m.evs ?? []).some((ev) => isConnected(ev)),
      );
      const hasProduction = meters.some((m) => {
        const solarConnected = (m.solar_arrays ?? []).some(isConnected);
        const genConnected = (m.fueled_generators ?? []).some(isConnected);
        return solarConnected || genConnected;
      });
      const hasStorage = meters.some((m) => {
        const battConnected = (m.batteries ?? []).some(isConnected);
        const evConnected = (m.evs ?? []).some(isConnected);
        return battConnected || evConnected;
      });

      setHasConnectedEVStorage(hasEVs);
      const isDERRequired = hasProduction || hasStorage;
      setIsDERRequired(isDERRequired);
    } catch (error) {
      console.error("Error fetching sysconfig:", error);
      setIsDERRequired(false);
      setHasConnectedEVStorage(false);
    }
  };

  const handleFileDelete = (label) => {
    setUploadedFiles((prev) => ({ ...prev, [label]: null }));
    setErrorMessages((prev) => ({ ...prev, [label]: "" }));
  };

  const validateInputs = () => {
    let errors = {};

    

    // Validate DER System Configuration file
    if (isDERRequired && !uploadedJSONFile) {
      
      errors["DER System Configuration"] =
        "DER System Configuration file is required";
    }

   
    if (!startDateTime) {
      errors.startDateTime = "Start Date-Time is required.";
    }

    if (!endDateTime) {
      errors.endDateTime = "End Date-Time is required.";
    } else if (new Date(endDateTime) <= new Date(startDateTime)) {
      errors.endDateTime = "End Date-Time must be later than Start Date-Time.";
    }

    setErrorMessages(errors);

    // Return whether validation passed
    return Object.keys(errors).length === 0;
  };

   const handleSimulate = async () => {
    const isValid = validateInputs();

    if (!isValid) {
      console.error("Simulation validation failed.");
      return;
    }

    const customerName = storageAccountName;
    const simulationName = customerSimulationName;

    

    // Utility function to convert the date/time to the expected format.
    const formatDateTime = (dateTimeString) => {
      const date = new Date(dateTimeString);

      const pad = (n) => n.toString().padStart(2, "0");

      const month = pad(date.getMonth() + 1); // Months are 0-based
      const day = pad(date.getDate());
      const year = date.getFullYear(); // 4-digit year
      const hours = pad(date.getHours()); // 24-hour format
      const minutes = pad(date.getMinutes());

      return `${month}/${day}/${year} ${hours}:${minutes}`;
    }

    if (isNaN(Date.parse(startDateTime))) {
      // Update the error state for startDateTime
      setErrorMessages((prevErrors) => ({
        ...prevErrors,
        startDateTime: "The date time field is invalid.",
      }));
      return;
    }

    if (isNaN(Date.parse(endDateTime))) {
      // Update the error state for endDateTime
      setErrorMessages((prevErrors) => ({
        ...prevErrors,
        endDateTime: "The date time field is invalid.",
      }));
      return;
    }

    // A random queue name for this run, cut to fit Azure's 63-character limit.
    const randomSequence = Math.floor(100000 + Math.random() * 900000);
    const reportChannelName = runQueueName(simulationName, randomSequence);

    // Prepare the request data.
    const requestData = {
      storage_account: customerName,
      microgrid_name: simulationName,

      ...(uploadedJSONFile && { der_sysconfig_json: uploadedJSONFile }),

      start_date_time: formatDateTime(startDateTime),
      end_date_time: formatDateTime(endDateTime),
      // report_period: reportingPeriod.toLowerCase(),
      report_channel_name: reportChannelName,
    };

    const headers = {
      "x-input-storage-manager-type":
        process.env.REACT_APP_SIMULATE_STORAGE_INPUT_TYPE,
      "x-output-storage-manager-type":
        process.env.REACT_APP_SIMULATE_STORAGE_OUTPUT_TYPE,
      "x-report-channel-type": process.env.REACT_APP_REPORT_CHANNEL_TYPE,
      Authorization: `Bearer ${token}`,
      "X-User-ID": sessionStorage.getItem("UserID") || "",
    };

    // Clear globals and initialize queue info.
    setErrorMessage("");
    setLog("");
    setTimeseriesFile("");
    setEconomicSummaryFile("");
    setDailysummaryFile("");
    const queueInfo = {
      customerName: customerName,
      queueName: reportChannelName,
      accessToken: token,
    };

    // Create queue, start simulation, display simulation log.
    try {
      // Create the queue.
      
      await createQueue(queueInfo);
      
      await simulateRun(requestData, headers, setErrorMessage);
      
      await displayLog(
        queueInfo,
        setLog,
        setTimeseriesFile,
        setEconomicSummaryFile,
        setDailysummaryFile,
        setSimulationFailureMessage,
        { handleButtonClick },
      );
      
      await deleteQueue(queueInfo);
      
    } catch (error) {
      setErrorMessage(error.message);
      // Delete queue after error.
      try {
        await deleteQueue(queueInfo, setErrorMessage);
      } catch (deleteErr) {
        console.error(
          `handleSimulateClick:Error deleting queue '${queueInfo} error message: ${deleteErr.message}`,
        );
      }
    }
  }

  const toggleDerLog = () => {
    setIsDerLogExpanded(!isDerLogExpanded);
  };

  const handleStartDateTimeChange = (e) => {
    let value = e.target.value;

    // Extract year from the datetime value
    const yearMatch = value.match(/^\d{4}/);
    if (yearMatch && yearMatch[0].length > 4) {
      // If year has more than 4 digits, truncate it
      value = value.replace(/^\d+/, yearMatch[0].substring(0, 4));
    }

    // Validate the year is between 1900 and 2100
    const year = parseInt(value.substring(0, 4));
    if (!isNaN(year) && (year < 1900 || year > 2100)) {
      return; // Don't update if year is invalid
    }

    setStartDateTime(value);

    // Auto-focus to end date input if input is complete
    if (value.length === 16) {
      // Full datetime-local format length
      if (endDateRef.current) {
        endDateRef.current.focus();
      }
    }
  };

  const handleEndDateTimeChange = (e) => {
    let value = e.target.value;

    // Extract year from the datetime value
    const yearMatch = value.match(/^\d{4}/);
    if (yearMatch && yearMatch[0].length > 4) {
      // If year has more than 4 digits, truncate it
      value = value.replace(/^\d+/, yearMatch[0].substring(0, 4));
    }

    // Validate the year is between 1900 and 2100
    const year = parseInt(value.substring(0, 4));
    if (!isNaN(year) && (year < 1900 || year > 2100)) {
      return; // Don't update if year is invalid
    }

    setEndDateTime(value);
  };

  return (
    <div className="simulations">
      
      {/* 2) NEW: Tab Navigation */}
      <div className="tabs">
        <button
          className={activeTab === "generate" ? "active" : ""}
          onClick={() => setActiveTab("generate")}
        >
          Generate Simulation
        </button>
        {/* <button
          className={activeTab === "history" ? "active" : ""}
          onClick={() => setActiveTab("history")}
        >
          History 
        </button> */}
      </div>

      {/* 3) Conditionally Render Based on activeTab */}
      {activeTab === "generate" && (
        <div className="main-grid">
          {/* ---- YOUR EXISTING SIMULATION UI CODE ---- */}
          {/* (Below is exactly what you had in your original return) */}

          {/* Input Time - Series CSV Section */}
          {/* <div className="input-card">
            <h3>Input Time - Series CSV</h3>
            <ul>
              {Object.keys(uploadedFiles).map((label, index) => (
                <li key={index} className="input-item">
                  <span
                    className={`checkmark ${
                      uploadedFiles[label] ? "uploaded" : ""
                    }`}
                  >
                    {uploadedFiles[label] ? "✔" : "○"}
                  </span>
                  <div className="file-label">
                    <span>
                      {label}
                      {index < 2 && <span className="required">*</span>}
                    </span>
                    {errorMessages[label] && (
                      <span className="error-message-uploadfiles">
                        {errorMessages[label]}
                      </span>
                    )}
                  </div>
                  <span className="file-status">
                    {truncateFileName(
                      uploadedFiles[label] || "No File has been uploaded"
                    )}
                  </span>
                  <label className="upload-icon">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => handleFileUpload(e, label)}
                      style={{ display: "none" }}
                    />
                    <MdOutlineFileUpload />
                  </label>
                  <button
                    className="delete-icon"
                    disabled={!uploadedFiles[label]}
                    onClick={() => handleFileDelete(label)}
                  >
                    <MdDeleteForever />
                  </button>
                </li>
              ))}
            </ul>
          </div> */}

          {/* DER System Configuration Section
      <div className="der-config-card">
            <span
              className={`der-config-checkmark ${
                uploadedJSONFile ? "der-config-uploaded" : ""
              }`}
            >
              {uploadedJSONFile ? "✔" : "○"}
            </span>
            <span className="der-config-title">
              DER System Configuration<span className="required">*</span>
              <span style={{ 
                fontStyle: 'italic', 
                fontSize: '12px',
                color: '#666',
                marginLeft: '8px',
                fontWeight: 'normal'
              }}>
                (required for solar generation uploads)
              </span>
            </span>
            <button
              className="der-config-generate-json"
              onClick={fetchAndBuildSchema}
            >
              Generate
            </button>
            <div className="der-config-file-status">
              {uploadedJSONFile || "No File has been uploaded"}
            </div>
            <label className="der-config-upload-icon">
              <input
                type="file"
                accept=".json"
                onChange={handleFileJSONUpload}
                style={{ display: "none" }}
              />
              <MdOutlineFileUpload />
            </label>
            {errorMessages["DER System Configuration"] && (
              <span className="der-config-error">
                {errorMessages["DER System Configuration"]}
              </span>
            )}
          </div> */}

          {/* DER System Configuration Section */}
          <div className="der-config-card">
            <span
              className={`der-config-checkmark ${uploadedJSONFile ? "der-config-uploaded" : ""}`}
            >
              {uploadedJSONFile ? "✔" : "○"}
            </span>
            <span className="der-config-title">
              Microgrid Configuration<span className="required">*</span>
              <span
                style={{
                  fontStyle: "italic",
                  fontSize: "12px",
                  color: "#666",
                  marginLeft: "8px",
                  fontWeight: "normal",
                }}
              >
                {/* (required for solar generation uploads) */}
              </span>
            </span>
            <button
              className="der-config-generate-json"
              onClick={fetchAndBuildSchema}
            >
              Generate
            </button>
            <div className="der-config-file-status">
              {uploadedJSONFile || "No File has been uploaded"}
            </div>
            <label className="der-config-upload-icon">
              <input
                type="file"
                accept=".json"
                onChange={handleFileJSONUpload}
                style={{ display: "none" }}
              />
              <MdOutlineFileUpload />
            </label>
            {errorMessages["DER System Configuration"] && (
              <span className="der-config-error">
                {errorMessages["DER System Configuration"]}
              </span>
            )}
          </div>

          {/* Miscellaneous Input Parameters Section */}
          <div className="misc-input">
            <h3>Miscellaneous Input Parameters</h3>
            <form>
              <div className="form-row">
                {/* <label className="form-item">
  Microgrid Name<span className="required">*</span>
  <input
    type="text"
    name="microgridName"
    value={sessionStorage.getItem("microgridName") || ""}
    readOnly
    
  />
</label> */}

                {/* <label className="form-item">
                  Demand Charge<span className="required">*</span>
                  <input
                    type="number"
                    name="demandCharge"
                    placeholder="0 - 100"
                    value={demandCharge}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "" || (value >= 0 && value <= 100)) {
                        setDemandCharge(value);
                      }
                    }}
                  />
                  {errorMessages.demandCharge && (
                    <span className="error-message-uploadfiles">
                      {errorMessages.demandCharge}
                    </span>
                  )}
                </label> */}
                {/* <label className="form-item">
                  Reporting Period<span className="required">*</span>
                  <select
                    name="reportPeriod"
                    value={reportingPeriod}
                    onChange={(e) => setReportingPeriod(e.target.value)}
                  >
                    {<option>---select---</option> }
                    <option>Daily</option>
                    <option>Monthly</option>
                    <option>Yearly</option>
                  </select>
                  {errorMessages.reportingPeriod && (
                    <span className="error-message-uploadfiles">
                      {errorMessages.reportingPeriod}
                    </span>
                  )}
                </label> */}
              </div>
              <div className="form-row">
                <label className="form-item">
                  Start Date<span className="required">*</span>
                  <input
                    type="datetime-local"
                    name="startDateTime"
                    ref={startDateRef}
                    value={startDateTime}
                    onChange={handleStartDateTimeChange}
                    min="1900-01-01T00:00"
                    max="2100-12-31T23:59"
                  />
                  {errorMessages.startDateTime && (
                    <span className="error-message-uploadfiles">
                      {errorMessages.startDateTime}
                    </span>
                  )}
                </label>
                <label className="form-item">
                  End Date<span className="required">*</span>
                  <input
                    type="datetime-local"
                    name="endDateTime"
                    ref={endDateRef}
                    value={endDateTime}
                    onChange={handleEndDateTimeChange}
                    min="1900-01-01T00:00"
                    max="2100-12-31T23:59"
                  />
                  {errorMessages.endDateTime && (
                    <span className="error-message-uploadfiles">
                      {errorMessages.endDateTime}
                    </span>
                  )}
                </label>
              </div>
              <button
                className="simulate-button"
                onClick={handleSimulate}
                type="button" // Explicitly set the type
                disabled={simulationInProgress}
              >
                {simulationInProgress ? "Simulating..." : "Start Simulation"}
              </button>
            </form>
          </div>

          {/* DER Twin Log Section */}
          <div className="der-log">
            <h3>
              Microgrid Simulation Log
              {/* <button
                className="toggle-button"
                onClick={toggleDerLog}
                disabled={simulationInProgress}
              >
                {isDerLogExpanded ? "Hide" : "Show"}
              </button> */}
            </h3>
            <div
              className={`log-output ${
                isDerLogExpanded ? "expanded" : "collapsed"
              }`}
            >
              <LogDisplay log={log} />
              {/* This area will continue to get populated with new logs */}
            </div>
          </div>

          {/* Results Section */}
          <div className="results">
            <h3>
              Results
              {/* <Chart data={fetchedData}
              triggerProcessing={triggerProcessing}
              /> */}
              {/* <DataProcessor
              //   triggerProcessing={triggerProcessing}
              //   csvData={fetchedData} // Pass fetched CSV data
              //   onViewsGenerated={(views) => {
              //     console.log("Generated Views:", views);
              //   }} */}
              {/* /> */}
            </h3>
            {simulationFailureMessage && (
              <div className="error-message" style={{ color: "red" }}>
                {simulationFailureMessage}
              </div>
            )}
            {/* Timeseries Results */}
            <div className="result-item">
              <span className="result-label">
                <b>Time Series: </b>

                <span className="timeseries-label">{timeseriesFile || ""}</span>
              </span>
              <button
                className="download-icon"
                onClick={() => handleDownload("Timeseries", timeseriesFile)}
                disabled={simulationInProgress || !timeseriesFile}
              >
                <MdOutlineFileDownload />
              </button>
              <button
                className="display-button"
                onClick={() => handleDisplay("Timeseries", timeseriesFile)}
                disabled={simulationInProgress || !timeseriesFile}
              >
                {displayedPreview === "Timeseries" ? "Hide" : "Display"}
              </button>
            </div>
            {displayedPreview === "Timeseries" &&
              timeseriesFileData?.length > 0 && (
                <div className="csv-previewer">
                  <table className="csv-tabler">
                    <thead>
                      <tr>
                        {Object.keys(timeseriesFileData[0]).map(
                          (header, index) => (
                            <th key={index}>{header}</th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {timeseriesFileData.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {Object.values(row).map((cell, cellIndex) => (
                            <td key={cellIndex}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            {/* Economic Summary Results */}
            <div className="result-item">
              <span className="result-label">
                <b> Daily Summary: </b>
                <span className="economic-summary-label">
                  {dailysummaryFile || ""}
                </span>
              </span>
              <button
                className="download-icon"
                onClick={() =>
                  handleDownload("Daily Summary", dailysummaryFile)
                }
                disabled={simulationInProgress || !dailysummaryFile}
              >
                <MdOutlineFileDownload />
              </button>
              <button
                className="display-button"
                onClick={() => handleDisplay("Daily Summary", dailysummaryFile)}
                disabled={simulationInProgress || !dailysummaryFile}
              >
                {displayedPreview === "Daily Summary" ? "Hide" : "Display"}
              </button>
            </div>
            {displayedPreview === "Daily Summary" &&
              dailysummaryadata?.length > 0 && (
                <div className="csv-previewer">
                  <table className="csv-tabler">
                    <thead>
                      <tr>
                        {Object.keys(dailysummaryadata[0]).map(
                          (header, index) => (
                            <th key={index}>{header}</th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {dailysummaryadata.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {Object.values(row).map((cell, cellIndex) => (
                            <td key={cellIndex}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      )}

      {/* 4) Render the NEW HistoryImport component on the "history" tab */}
      {/* {activeTab === "history" && <HistoryImport  />} */}
    </div>
  );
}

export default Simulations;
